// X 账号管理页 — 运维 X (Twitter) session cookies.
//
// 我们不在浏览器侧存任何 cookies，只是把操作员粘贴的 auth_token + ct0
// 直接 POST 给后端 → 后端转发给 twscrape sidecar → sidecar 写本地 SQLite。
// 列表回来的状态同样是穿透到 sidecar 的实时拉取，所以 active=false / error_msg
// 显示的是 X 真实拒绝的最新信号，而不是中间缓存。
//
// 失效检测 = 上方红色警告条；点 "立刻校验" 触发后端调一次 sidecar 看一眼
// elonmusk 是否还能解析，秒级反馈 cookies 是否被 X 杀掉了。

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints, APIError, type AddXAccountRequest, type XAccountStatus } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { SectionTabs, SYSTEM_TABS } from "@/components/SectionTabs";

export default function XAccounts() {
  const qc = useQueryClient();
  const accountsQ = useQuery({
    queryKey: ["x-accounts"],
    queryFn: () => endpoints.xAccounts(),
    refetchInterval: 30_000,
  });
  const healthQ = useQuery({
    queryKey: ["x-accounts-health"],
    queryFn: () => endpoints.xAccountsHealth(),
    refetchInterval: 30_000,
  });

  const addM = useMutation({
    mutationFn: (body: AddXAccountRequest) => endpoints.addXAccount(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["x-accounts"] });
      qc.invalidateQueries({ queryKey: ["x-accounts-health"] });
    },
  });

  const delM = useMutation({
    mutationFn: (username: string) => endpoints.deleteXAccount(username),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["x-accounts"] }),
  });

  const verifyM = useMutation({
    mutationFn: () => endpoints.verifyXAccounts(),
    onSuccess: (r) => {
      alert(`✓ ${r.message}`);
      qc.invalidateQueries({ queryKey: ["x-accounts"] });
    },
    onError: (e: unknown) => {
      if (e instanceof APIError && e.status === 401) {
        alert("✗ Cookies 已失效——请重新登录账号并粘贴新 cookies。");
      } else {
        alert(`✗ 校验失败：${e instanceof Error ? e.message : String(e)}`);
      }
      qc.invalidateQueries({ queryKey: ["x-accounts"] });
    },
  });

  // 表单本地态——刻意不写进 URL / global store，密钥不该在地址栏。
  const [form, setForm] = useState<AddXAccountRequest>({ username: "", auth_token: "", ct0: "" });

  const accounts: XAccountStatus[] = accountsQ.data ?? [];
  const health = healthQ.data;
  const activeCount = accounts.filter((a) => a.active).length;
  const inactiveCount = accounts.length - activeCount;
  // Three states the operator needs to distinguish at a glance:
  //   - allDead: pool has accounts but all failed → X抓取整体停摆，红色严重告警
  //   - someDead: 部分账号失活，黄色 actionable warning
  //   - allActive: 全好，no banner
  const allDead = accounts.length > 0 && activeCount === 0;
  const someDead = inactiveCount > 0 && activeCount > 0;

  return (
    <div className="flex h-full flex-col">
      <SectionTabs tabs={SYSTEM_TABS} />
      <header className="border-b border-border bg-background px-6 py-4">
        <div className="flex items-baseline gap-4">
          <h1 className="text-lg font-semibold">X 账号 / Cookies 管理</h1>
          <p className="text-sm text-muted-foreground">
            twscrape sidecar pool — {accounts.length} 个账号，
            {accounts.filter((a) => a.active).length} 个活跃
          </p>
          <div className="ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => verifyM.mutate()}
              disabled={verifyM.isPending || accounts.length === 0}
            >
              {verifyM.isPending ? "校验中…" : "立刻校验 cookies"}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Sidecar 状态 */}
        {health && !health.reachable && (
          <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
            ⚠️ sidecar 不可达：{health.error ?? "未知错误"}。
            <br />检查 systemd 服务：<code>systemctl status twscrape-sidecar</code>
          </div>
        )}

        {/* 全员阵亡 — X 抓取已停摆 */}
        {allDead && (
          <div className="rounded-md border-2 border-red-400 bg-red-50 px-4 py-3 text-sm text-red-800">
            🚨 <strong>X 抓取已停摆</strong>：池里 {accounts.length} 个账号全部失效。
            <br />调度器会持续 401，相关 job 不会更新数据。
            <strong>请立即至少恢复一个</strong>：浏览器重新登录任一账号 → 复制新 auth_token + ct0 → 下方表单替换。
          </div>
        )}

        {/* 部分失活 — 仍可工作但需关注 */}
        {someDead && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            ⚠️ 有 {inactiveCount} / {accounts.length} 个账号失效（cookies 死或被风控）。
            X 抓取仍在工作（{activeCount} 个号活跃），但吞吐降了。重新登录失效账号 → F12 复制 cookies → 下方表单替换。
          </div>
        )}

        {/* 添加表单 */}
        <section className="rounded-md border border-border bg-background p-4">
          <h2 className="font-medium mb-3">添加 / 替换账号</h2>
          <p className="text-xs text-muted-foreground mb-4">
            操作步骤：浏览器登录 X 账号 → F12 → Application → Cookies → x.com →
            复制 <code className="bg-muted px-1">auth_token</code> 和{" "}
            <code className="bg-muted px-1">ct0</code> 的 Value（或者直接复制 Request
            Headers 里的 Cookie 整行,下面"批量粘贴"自动解析）。
            <br />
            <span className="text-red-600">取完不要登出</span>——登出会让 cookies 服务端作废。
          </p>
          {/* v2.4.3 — paste-and-parse: operator copies the entire
              Cookie: header row from DevTools Network tab, presses Parse,
              and we extract auth_token + ct0. Saves three copy-paste
              cycles per account. */}
          <CookiePasteHelper
            onParsed={(parsed) =>
              setForm((f) => ({
                ...f,
                auth_token: parsed.auth_token || f.auth_token,
                ct0: parsed.ct0 || f.ct0,
              }))
            }
          />
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              addM.mutate(form, {
                onSuccess: () => setForm({ username: "", auth_token: "", ct0: "" }),
              });
            }}
          >
            <div className="grid gap-1">
              <label className="text-sm font-medium">用户名（不带 @）</label>
              <input
                className="rounded-md border border-border px-3 py-1.5 text-sm font-mono"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="elonmusk"
                required
              />
            </div>
            <div className="grid gap-1">
              <label className="text-sm font-medium">auth_token（40 位 hex）</label>
              <input
                className="rounded-md border border-border px-3 py-1.5 text-sm font-mono"
                value={form.auth_token}
                onChange={(e) => setForm((f) => ({ ...f, auth_token: e.target.value }))}
                placeholder="89d7c8f08fb593076117a4751b00d717a8890772"
                required
                spellCheck={false}
              />
            </div>
            <div className="grid gap-1">
              <label className="text-sm font-medium">ct0（更长的 hex）</label>
              <input
                className="rounded-md border border-border px-3 py-1.5 text-sm font-mono"
                value={form.ct0}
                onChange={(e) => setForm((f) => ({ ...f, ct0: e.target.value }))}
                placeholder="489091c32085d6d6ea4c465ff001ec2d32e90d49..."
                required
                spellCheck={false}
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={addM.isPending}>
                {addM.isPending ? "添加中…" : "保存"}
              </Button>
              {addM.isError && (
                <span className="text-sm text-red-600">
                  {addM.error instanceof Error ? addM.error.message : String(addM.error)}
                </span>
              )}
              {addM.isSuccess && <span className="text-sm text-green-700">✓ 已添加</span>}
            </div>
          </form>
        </section>

        {/* 账号列表 */}
        <section className="rounded-md border border-border bg-background">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="font-medium">已注册账号</h2>
            <span className="text-xs text-muted-foreground">
              每 30s 自动刷新；状态来自 sidecar 实时回报
            </span>
          </div>
          {accountsQ.isLoading && <div className="p-4 text-sm text-muted-foreground">加载中…</div>}
          {accountsQ.isError && (
            <div className="p-4 text-sm text-red-600">
              拉取失败：
              {accountsQ.error instanceof Error ? accountsQ.error.message : String(accountsQ.error)}
            </div>
          )}
          {accountsQ.data && accounts.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">还没有账号——用上面的表单添加。</div>
          )}
          {accounts.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">用户名</th>
                  <th className="px-4 py-2 text-left">状态</th>
                  <th className="px-4 py-2 text-left">最近使用</th>
                  <th className="px-4 py-2 text-right">总请求</th>
                  <th className="px-4 py-2 text-left">错误</th>
                  <th className="px-4 py-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.username} className="border-t border-border">
                    <td className="px-4 py-2 font-mono">@{a.username}</td>
                    <td className="px-4 py-2">
                      {a.active ? (
                        <span className="text-green-700">● 活跃</span>
                      ) : (
                        <span className="text-red-600">○ 失效</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{a.last_used ?? "—"}</td>
                    <td className="px-4 py-2 text-right">{a.total_req}</td>
                    <td className="px-4 py-2 text-xs text-red-600 max-w-[300px] truncate">
                      {a.error_msg ?? ""}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (confirm(`删除账号 @${a.username}？这只是从 sidecar 移除，X 本身不受影响。`)) {
                            delM.mutate(a.username);
                          }
                        }}
                        disabled={delM.isPending}
                      >
                        移除
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}

// CookiePasteHelper — paste a cookie blob (DevTools Network "Cookie:" header
// or document.cookie output), pull out auth_token + ct0. v2.4.3 — replaces
// the 3-input copy-paste cycle with a single "paste raw" + parse click.
//
// Supported formats (whichever the operator copies):
//   Cookie: auth_token=89d...; ct0=4890...; guest_id=...
//   auth_token=89d...; ct0=4890...
//   { auth_token: "89d...", ct0: "4890..." }   ← also accept just for kindness
function CookiePasteHelper({ onParsed }: { onParsed: (parsed: { auth_token?: string; ct0?: string }) => void }) {
  const [raw, setRaw] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  const parse = () => {
    const text = raw.trim();
    if (!text) {
      setStatus("空内容,无法解析");
      return;
    }
    // Greedy: any string of the form `name=VALUE` (40+ hex/url-safe chars).
    // We accept both ; and , as separators to handle "Cookie: ..." headers
    // and JSON-ish formats alike.
    const auth = /auth_token\s*[=:]\s*"?([A-Za-z0-9_-]{20,})/i.exec(text);
    const ct0 = /\bct0\s*[=:]\s*"?([A-Za-z0-9_-]{20,})/i.exec(text);
    const parsed = {
      auth_token: auth?.[1],
      ct0: ct0?.[1],
    };
    if (!parsed.auth_token && !parsed.ct0) {
      setStatus("没找到 auth_token 或 ct0,请检查粘贴内容");
      return;
    }
    onParsed(parsed);
    const got = [
      parsed.auth_token ? "auth_token ✓" : "auth_token 未找到",
      parsed.ct0 ? "ct0 ✓" : "ct0 未找到",
    ].join(" · ");
    setStatus(`解析完毕:${got}`);
  };

  return (
    <div className="mb-4 rounded-md border border-dashed border-border bg-muted/30 p-3">
      <div className="mb-2 flex items-center gap-2 text-xs">
        <span className="font-medium">批量粘贴</span>
        <span className="text-muted-foreground">
          可直接粘贴 DevTools Network → 任一请求 → Cookie header 整行
        </span>
      </div>
      <textarea
        className="h-16 w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="auth_token=89d7...; ct0=4890..."
        spellCheck={false}
      />
      <div className="mt-2 flex items-center gap-3">
        <Button type="button" size="sm" variant="outline" onClick={parse}>
          解析并填入
        </Button>
        {status && <span className="text-xs text-muted-foreground">{status}</span>}
      </div>
    </div>
  );
}
